/**
 * Auth Token Manager
 * Manages Firebase auth token for API requests
 * Token is updated whenever auth state changes
 */

import { logAuth, logger } from '../../utils/logger';

let currentToken: string | null = null;
let tokenPromise: Promise<string | null> | null = null;
let onSessionExpiredCallback: (() => void) | null = null;
let sessionExpiredTriggered = false;
let onAccountSuspendedCallback: (() => void) | null = null;
let accountSuspendedTriggered = false;
let onRegistrationClosedCallback: (() => void) | null = null;
let registrationClosedTriggered = false;

/**
 * Register a callback to be called when API returns 401 (session expired)
 * Called by AuthProvider to wire up signOut
 */
export function onSessionExpired(callback: () => void): void {
  onSessionExpiredCallback = callback;
  sessionExpiredTriggered = false;
}

/**
 * Notify that session has expired (401 from API)
 * Triggers sign-out via the registered callback
 * Guarded against multiple concurrent calls
 */
export function notifySessionExpired(): void {
  if (sessionExpiredTriggered) return;
  sessionExpiredTriggered = true;
  if (onSessionExpiredCallback) {
    onSessionExpiredCallback();
  }
}

/**
 * Register callback saat akun ditangguhkan (403 ACCOUNT_SUSPENDED dari gdpr/erase)
 */
export function onAccountSuspended(callback: () => void): void {
  onAccountSuspendedCallback = callback;
  accountSuspendedTriggered = false;
}

/**
 * Trigger saat 403 ACCOUNT_SUSPENDED diterima — force signOut + redirect
 */
export function notifyAccountSuspended(): void {
  if (accountSuspendedTriggered) return;
  accountSuspendedTriggered = true;
  if (onAccountSuspendedCallback) {
    onAccountSuspendedCallback();
  }
}

/**
 * Register callback saat backend menolak membuat akun baru (403 REGISTRATION_CLOSED, sejak 27 Sep 2026)
 */
export function onRegistrationClosed(callback: () => void): void {
  onRegistrationClosedCallback = callback;
  registrationClosedTriggered = false;
}

/**
 * Trigger saat 403 REGISTRATION_CLOSED diterima: force signOut + arahkan ke halaman daftar
 */
export function notifyRegistrationClosed(): void {
  if (registrationClosedTriggered) return;
  registrationClosedTriggered = true;
  if (onRegistrationClosedCallback) {
    onRegistrationClosedCallback();
  }
}

/**
 * Set the current auth token
 * Called by AuthProvider when auth state changes
 */
export function setAuthToken(token: string | null): void {
  currentToken = token;
  logAuth(token ? 'Token updated' : 'Token cleared');
}

/**
 * Get the current auth token
 * Returns immediately if token is available
 */
export function getAuthToken(): string | null {
  return currentToken;
}

/**
 * Get auth token with fallback to Firebase
 * Used when token might not be set yet
 */
export async function getAuthTokenAsync(): Promise<string | null> {
  // If token is already available, return it
  if (currentToken) {
    return currentToken;
  }

  // If already fetching, wait for that promise
  if (tokenPromise) {
    return tokenPromise;
  }

  // Try to get token from Firebase as fallback
  tokenPromise = (async () => {
    try {
      const { auth } = await import('../firebase/config');

      // Wait for auth state with timeout
      const user = await new Promise<typeof auth.currentUser>((resolve) => {
        if (auth.currentUser) {
          resolve(auth.currentUser);
          return;
        }

        const unsubscribe = auth.onAuthStateChanged((u) => {
          unsubscribe();
          resolve(u);
        });

        // Timeout after 5 seconds
        setTimeout(() => {
          unsubscribe();
          resolve(null);
        }, 5000);
      });

      if (user) {
        const token = await user.getIdToken(true);
        currentToken = token;
        return token;
      }

      return null;
    } catch (error) {
      logger.error('Error getting auth token', undefined, error instanceof Error ? error : undefined);
      return null;
    } finally {
      tokenPromise = null;
    }
  })();

  return tokenPromise;
}

/**
 * Clear the auth token
 * Called on sign out - also notifies backend to blacklist the token
 */
export async function clearAuthToken(): Promise<void> {
  currentToken = null;
  // Backend token revocation is handled by AuthService.signOut()
  // to avoid double /api/users/logout calls (second one gets 401)
  logAuth('Token cleared');
}
